import { isAxiosError } from 'axios';

import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import { toCamelCase } from '@/lib/case';

import { useAuthAxios } from '@/hooks/useAuthAxios';

import { AttachmentRef } from '@/features/Feedback/types';

import { RepositoryDocument, RepositoryDocumentPage } from './types';

const SEARCH_LIMIT = 50;

type Fields = Record<string, unknown>;

const isRecord = (value: unknown): value is Fields =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const isStringOrNull = (value: unknown): boolean =>
    value === null || typeof value === 'string';

const isRepositoryDocument = (value: unknown): value is RepositoryDocument =>
    isRecord(value) &&
    typeof value.id === 'number' &&
    typeof value.fileId === 'number' &&
    typeof value.fileName === 'string' &&
    typeof value.type === 'string' &&
    typeof value.typeLabel === 'string' &&
    typeof value.date === 'string' &&
    isStringOrNull(value.reference) &&
    isStringOrNull(value.title) &&
    isStringOrNull(value.asset);

const isRepositoryDocumentPage = (
    value: unknown,
): value is RepositoryDocumentPage =>
    isRecord(value) &&
    typeof value.total === 'number' &&
    Array.isArray(value.results) &&
    value.results.every(isRepositoryDocument);

/** True when the user cannot browse documents (no team or no permission). */
export const isDocumentsUnavailable = (error: unknown): boolean => {
    if (!isAxiosError(error)) return false;
    const status = error.response?.status;
    return status === 403 || status === 404;
};

/** Team-wide document search; an empty query lists the most recent documents. */
export const useSearchRepositoryDocuments = (query: string) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['feedback-documents', query],
        queryFn: async () => {
            const res = await client.get('/api/feedback/documents', {
                params: { q: query, limit: SEARCH_LIMIT },
            });
            const page: unknown = toCamelCase<unknown>(res.data);
            if (!isRepositoryDocumentPage(page)) {
                throw new Error('Unexpected document search response');
            }
            return page;
        },
        enabled,
        retry: false,
        placeholderData: keepPreviousData,
    });
};

/** Ask the backend to copy a document's original file to the license server. */
export const useCopyRepositoryDocument = () => {
    const { client } = useAuthAxios();

    return useMutation({
        mutationFn: async (docId: number) => {
            const res = await client.post(
                `/api/feedback/documents/${docId}/copy`,
            );
            return toCamelCase<AttachmentRef>(res.data);
        },
    });
};
